/**
 * Mobile Sort By: custom white panel (native <select> pickers vary by OS — dark/tiny on some phones).
 * Desktop (≥992px): native select stays as-is; custom UI is hidden.
 */
class FacetSortDropdown extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select[name="sort_by"]');
    if (!this.select) return;

    this.selectBox = this.select.closest('.select') || this;

    if (!this.querySelector('.facet-sort-dropdown__panel')) {
      this.buildUI();
    } else {
      this.trigger = this.querySelector('.facet-sort-dropdown__trigger');
      this.panel = this.querySelector('.facet-sort-dropdown__panel');
      this.labelEl = this.querySelector('.facet-sort-dropdown__label');
    }

    this.syncFromSelect();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  buildUI() {
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'facet-sort-dropdown__trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.innerHTML = '<span class="facet-sort-dropdown__label"></span>';
    this.labelEl = this.trigger.querySelector('.facet-sort-dropdown__label');

    this.panel = document.createElement('ul');
    this.panel.className = 'facet-sort-dropdown__panel';
    this.panel.setAttribute('role', 'listbox');
    this.panel.hidden = true;

    this.appendChild(this.trigger);
    this.appendChild(this.panel);
  }

  syncFromSelect() {
    if (!this.select || !this.panel) return;

    const selected = this.select.options[this.select.selectedIndex];
    if (this.labelEl) {
      this.labelEl.textContent = selected ? selected.textContent.trim() : '';
    }

    this.panel.innerHTML = '';
    Array.from(this.select.options).forEach((opt) => {
      const li = document.createElement('li');
      li.className = 'facet-sort-dropdown__option';
      li.setAttribute('role', 'option');
      li.dataset.value = opt.value;
      li.textContent = opt.textContent.trim();
      if (opt.selected) {
        li.classList.add('is-selected');
        li.setAttribute('aria-selected', 'true');
      } else {
        li.setAttribute('aria-selected', 'false');
      }
      this.panel.appendChild(li);
    });
  }

  bindEvents() {
    if (this._bound) return;
    this._bound = true;

    this._onTriggerClick = (e) => {
      if (!this.isMobile()) return;
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    };

    this._onPanelClick = (e) => {
      const option = e.target.closest('.facet-sort-dropdown__option');
      if (!option || !this.panel.contains(option)) return;
      e.preventDefault();
      e.stopPropagation();
      this.choose(option.dataset.value);
    };

    this._onDocPointer = (e) => {
      if (!this.isOpen()) return;
      if (this.contains(e.target)) return;
      if (this.selectBox && this.selectBox.contains(e.target)) return;
      this.close();
    };

    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
        if (this.trigger) this.trigger.focus();
      }
    };

    this._onSelectChange = () => {
      this.syncFromSelect();
    };

    this.trigger.addEventListener('click', this._onTriggerClick);
    this.panel.addEventListener('click', this._onPanelClick);
    document.addEventListener('click', this._onDocPointer);
    document.addEventListener('keydown', this._onKeydown);
    this.select.addEventListener('change', this._onSelectChange);
  }

  unbindEvents() {
    if (!this._bound) return;
    this._bound = false;
    if (this.trigger) this.trigger.removeEventListener('click', this._onTriggerClick);
    if (this.panel) this.panel.removeEventListener('click', this._onPanelClick);
    document.removeEventListener('click', this._onDocPointer);
    document.removeEventListener('keydown', this._onKeydown);
    if (this.select) this.select.removeEventListener('change', this._onSelectChange);
  }

  isMobile() {
    return window.matchMedia('(max-width: 991px)').matches;
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  toggle() {
    if (this.isOpen()) this.close();
    else this.open();
  }

  open() {
    if (!this.isMobile()) return;
    document.querySelectorAll('facet-sort-dropdown.is-open').forEach((el) => {
      if (el !== this && typeof el.close === 'function') el.close();
    });
    this.syncFromSelect();
    this.classList.add('is-open');
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    if (this.panel) this.panel.hidden = true;
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
  }

  choose(value) {
    if (!this.select || value == null) return;
    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.syncFromSelect();
    this.close();
  }
}

customElements.define('facet-sort-dropdown', FacetSortDropdown);
